/**
 * Mention provider registry.
 *
 * Features register a MentionProvider to participate in @mention autocomplete
 * across todo titles and descriptions. If no providers are registered, the
 * `@` character is just regular text — no dropdown, no parsing.
 *
 * Storage format (inline markdown-like): `[@DisplayName](kind:id)`
 */

import React from 'react';

export interface MentionMatch {
  /** Stable id for the kind (e.g. "123" for a contact). */
  id: string;
  /** Display label shown in the dropdown and inserted into the text. */
  label: string;
  /** Optional secondary hint line (role, organization, …). */
  hint?: string;
  /** Optional avatar image URL. */
  avatarUrl?: string;
}

export interface MentionProvider {
  /** e.g. "contact" */
  kind: string;
  /** Single character that triggers autocomplete — v1 only supports "@". */
  trigger: string;
  /** Return ranked matches for the given query. Empty query → []. */
  search(q: string): Promise<MentionMatch[]>;
  /** Render a chip for this kind in display mode. */
  renderChip(props: { id: string; label: string }): React.ReactNode;
  /** Optional click handler — e.g. navigate to a detail modal. */
  onChipClick?(id: string): void;
  /** Fired the moment a match is inserted into an input. Providers use this
   *  to e.g. auto-enable a contact so downstream surfaces can resolve the id. */
  onInsert?(match: MentionMatch): void;
}

const providers = new Map<string, MentionProvider>();

export function registerMentionProvider(p: MentionProvider): void {
  providers.set(p.kind, p);
}

export function getMentionProvider(kind: string): MentionProvider | undefined {
  return providers.get(kind);
}

export function getAllMentionProviders(): MentionProvider[] {
  return Array.from(providers.values());
}

export function hasAnyProvider(): boolean {
  return providers.size > 0;
}
